import { Accordion, AccordionSummary, Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, OutlinedInput, Select, Stack } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "../../../../pages/workbench_build/WorkbenchBuild";
import { eachDeep } from "deepdash-es/standalone";
import { cloneDeep, debounce, get, isArray, isEqual, min, set } from "lodash";
import { api } from "../../../../redux/api";
import TDataOutboundDependency from "./TDataOutboundDependency";
import axios from "axios";
import OutboundConditionModal from "./OutboundConditionModal";


export default function OutboundConditionControls({ treeData, setTreeData, treeSelectedNode, setTreeSelectedNode, parentNode }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree, leftTree } = useSelector(state => state.treeData[parentScreen]);
  const { translatedData } = rightTree.tree.body;
  const selectedNode = rightTree.tree.body.selectedNode;
  const { baseUrl, user, token, account } = useSelector(state => state.queryParams);

  //main save function. clone data, find node in tdata, change obj, dispatch new tdata and selnode
  const submitChange = (key, value, bulk, removeValues) => {
    if (!key && !removeValues && !bulk) return;
    // Clone and deeply iterate through the tree data to update the relevant node.
    const updatedData = eachDeep(cloneDeep(treeData), (obj) => {
      // if (obj?.key !== treeSelectedNode?.key) return;

      if (obj?.key === treeSelectedNode?.key) {
        if (key === "NAME") {
          if (value === "" || value === null || value === undefined) {
            obj.NAME = "UNNAMED";
            if (obj.hasOwnProperty("OUTBOUND_NAME")) delete obj.OUTBOUND_NAME;
          } else {
            obj.NAME = value;
            obj.OUTBOUND_NAME = value;
          }

        } else if (key === "VALIDATE_DATA") {
          if (value === "TRUE") {
            obj.VALIDATE_DATA = "TRUE";
            obj.ELEMENT_VALIDATE_DATA = "TRUE";
            obj.SEGMENT_VALIDATE_DATA = "TRUE";
            obj.TRANSACTIONSET_VALIDATE_DATA = "TRUE";
            obj.FUNCTIONAL_GROUP_VALIDATE_DATA = "TRUE";
          } else {
            if (obj?.hasOwnProperty('VALIDATE_DATA')) delete obj['VALIDATE_DATA'];
            if (obj?.hasOwnProperty('ELEMENT_VALIDATE_DATA')) delete obj['ELEMENT_VALIDATE_DATA'];
            if (obj?.hasOwnProperty('SEGMENT_VALIDATE_DATA')) delete obj['SEGMENT_VALIDATE_DATA'];
            if (obj?.hasOwnProperty('TRANSACTIONSET_VALIDATE_DATA')) delete obj['TRANSACTIONSET_VALIDATE_DATA'];
            if (obj?.hasOwnProperty('FUNCTIONAL_GROUP_VALIDATE_DATA')) delete obj['FUNCTIONAL_GROUP_VALIDATE_DATA'];
            if (obj?.hasOwnProperty('STOP_ON_FAILED_OUTBOUND')) delete obj['STOP_ON_FAILED_OUTBOUND'];
          }
        } else {
          // Update other properties directly on the object.
          if (value === null) {
            if (obj?.hasOwnProperty(key)) delete obj[key];
          } else obj[key] = value;
        }
        setTreeSelectedNode(obj);
      } 

      

      // If the object has children, return them for further iteration.
      if (isArray(obj?.children)) {
        return obj.children;
      }
    });

    setTreeData(updatedData);
  };
  
  const debounceChangeHandler = useCallback(debounce(submitChange, 100), [rightTree.tree]);

  //*** "Name" - NAME - text field ***//
  const defaultName = treeSelectedNode?.OUTBOUND_NAME || "";
  const [name, setName] = useState(defaultName);

  // Handle changes to the data name, specific logic for onChange event
  const handleNameChange = (e) => {
    if (e?.target?.value || e?.target?.value === '') {
      setName(e.target.value);
      if (e?.target?.value === '' || e?.target?.value === undefined || e?.target?.value === null) {
        debounceChangeHandler('NAME', null);
      } else {
        debounceChangeHandler('NAME', e.target.value);
      }
    }
  }

  //datatype defines what checkboxes are available
  const [templateDatatype, setTemplateDatatype] = useState("");
  const [validTemplateSelected, setValidTemplateSelected] = useState(false);
  // console.log("templateDatatype", templateDatatype);

  //*** "Validate Inbound Data" - VALIDATE_DATA - bool checkbox ***//
  const defaultValidateDataChecked = treeSelectedNode?.VALIDATE_DATA === "TRUE" ? true : false;
  const [validateDataChecked, setValidateDataChecked] = useState(defaultValidateDataChecked);

  const handleValidateDataChange = (e) => {
    const checked = e?.target?.checked;
    setValidateDataChecked(checked);
    debounceChangeHandler('VALIDATE_DATA', checked ? 'TRUE' : null);
    if (checked) {
      setElementValidateChecked(true);
      setSegmentValidateChecked(true);
      setTransactionSetValidateChecked(true);
      setFunctionalGroupValidateChecked(true);
    } else {
      setElementValidateChecked(false);
      setSegmentValidateChecked(false);
      setTransactionSetValidateChecked(false);
      setFunctionalGroupValidateChecked(false);
      setStopOnFailedChecked(false);
    }
  }

  //*** "Element Validation" - ELEMENT_VALIDATE_DATA - bool checkbox ***//
  const defaultElementValidateChecked = treeSelectedNode?.ELEMENT_VALIDATE_DATA === "TRUE" ? true : false;
  const [elementValidateChecked, setElementValidateChecked] = useState(defaultElementValidateChecked);

  const handleElementValidateChange = (e) => {
    const checked = e?.target?.checked;
    setElementValidateChecked(checked);
    debounceChangeHandler('ELEMENT_VALIDATE_DATA', checked ? 'TRUE' : null);
  }

  //*** "Segment Validation" - SEGMENT_VALIDATE_DATA - bool checkbox ***//
  const defaultSegmentValidateChecked = treeSelectedNode?.SEGMENT_VALIDATE_DATA === "TRUE" ? true : false;
  const [segmentValidateChecked, setSegmentValidateChecked] = useState(defaultSegmentValidateChecked);

  const handleSegmentValidateChange = (e) => {
    const checked = e?.target?.checked;
    setSegmentValidateChecked(checked);
    debounceChangeHandler('SEGMENT_VALIDATE_DATA', checked ? 'TRUE' : null);
  }

  //*** "Transaction Set Validation" - TRANSACTIONSET_VALIDATE_DATA - bool checkbox ***//
  const defaultTransactionSetValidateChecked = treeSelectedNode?.TRANSACTIONSET_VALIDATE_DATA === "TRUE" ? true : false;
  const [transactionSetValidateChecked, setTransactionSetValidateChecked] = useState(defaultTransactionSetValidateChecked);

  const handleTransactionSetValidateChange = (e) => {
    const checked = e?.target?.checked;
    setTransactionSetValidateChecked(checked);
    debounceChangeHandler('TRANSACTIONSET_VALIDATE_DATA', checked ? 'TRUE' : null);
  }

  //*** "Functional Group Validation" - FUNCTIONAL_GROUP_VALIDATE_DATA - bool checkbox ***//
  const defaultFunctionalGroupValidateChecked = treeSelectedNode?.FUNCTIONAL_GROUP_VALIDATE_DATA === "TRUE" ? true : false;
  const [functionalGroupValidateChecked, setFunctionalGroupValidateChecked] = useState(defaultFunctionalGroupValidateChecked);

  const handleFunctionalGroupValidateChange = (e) => {
    const checked = e?.target?.checked;
    setFunctionalGroupValidateChecked(checked);
    debounceChangeHandler('FUNCTIONAL_GROUP_VALIDATE_DATA', checked ? 'TRUE' : null);
  }

  //*** "Stop Translation on Error" - STOP_ON_FAILED_OUTBOUND - bool checkbox ***//
  const defaultStopOnFailedChecked = treeSelectedNode?.STOP_ON_FAILED_OUTBOUND === "TRUE" ? true : false;
  const [stopOnFailedChecked, setStopOnFailedChecked] = useState(defaultStopOnFailedChecked);

  const handleStopOnFailedChange = (e) => {
    const checked = e?.target?.checked;
    setStopOnFailedChecked(checked);
    debounceChangeHandler('STOP_ON_FAILED_OUTBOUND', checked ? 'TRUE' : null);
  }

  const handleOutboundModalSave = (newData) => {
    if (!newData) return;

    debounceChangeHandler("CONDITION", newData);
  };

  // validTemplateSelected is now managed as state and set by TDataOutboundDependency

  useEffect(() => {
    //we have to fetch the template data from the server to know what datatype it is. can't find this info in the inbound WFGROUP tree data
    const templateGroupValue = treeSelectedNode?.TEMPLATE_LOCATION?.split("/")?.[1];
    const templateValue = treeSelectedNode?.TEMPLATE?.split(".")?.[0];

    if (
      templateGroupValue 
      && templateValue 
      && !["SELECT", "EMPTY_COMBO", ""].includes(templateValue)
    ) {
      axios.get(`${baseUrl}/workbench`, {
        params: {
          action: 'get_outbound_template_datatype',
          user: user,
          account: account,
          workflow_group_name: templateGroupValue,
          template_name: templateValue,
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }).then((res) => {
        if (res?.data?.datatype && res?.data?.datatype !== "null") {
          setTemplateDatatype(res.data.datatype);
        } else {
          setTemplateDatatype("CSV");
        }
      }).catch((err) => {
        console.warn(err);
        setTemplateDatatype("");
      });
    } else {
      // No valid template selected, reset datatype
      setTemplateDatatype("");
    }
  }, [treeSelectedNode.TEMPLATE_LOCATION, treeSelectedNode.TEMPLATE, treeSelectedNode.tgval, treeSelectedNode.tval]);
  
  return (
    <div style={{marginTop: "20px"}}>

      {/* "Name" text field */}
      <FormControl variant='outlined' className='width-100' size='small'>
        <InputLabel
          htmlFor='ib-cond-name-input'
          sx={{ padding: "0 6px !important", left: "-5px" }}
        >
          {t('LBL_OUT_COND_NAME').replace(":", "")}
        </InputLabel>
        <OutlinedInput
          id="ib-cond-name-input"
          size='small'
          variant='outlined'
          fullWidth
          autoComplete='off'
          value={name}
          // endAdornment={<div style={styles.getDataNameAdornment}>{`ID: ${wfItemId}`}</div>}
          // sx={{ justifyContent: "flex-end" }}
          onChange={(e) => handleNameChange(e)}
          // onBlur={(e) => handleNameBlur(e?.target?.value, e)}
        />
      </FormControl>

      {/* Selected Inbound Template to Use */}
      <div style={{marginTop: "10px"}}>
        <div style={{fontSize: "12px", color: "#000", marginBottom: "10px"}}>{t('LBL_SELECT_TEMPLATE')}</div>
        <TDataOutboundDependency 
          treeData={treeData} 
          setTreeData={setTreeData} 
          treeSelectedNode={treeSelectedNode}
          setTreeSelectedNode={setTreeSelectedNode}
          templateDatatype={templateDatatype}
          setTemplateDatatype={setTemplateDatatype}
          parentNode={parentNode}
          onValidationChange={setValidTemplateSelected}
        />
      </div>

      {/* "Validate Outbound Data" checkbox */}
      {(!['DATABASE', 'XML'].includes(templateDatatype) && templateDatatype !== "") && (
        <FormControl variant='outlined' className='width-100' size='small'>
          <FormControlLabel
            control={
              <Checkbox
                checked={validateDataChecked}
                onChange={handleValidateDataChange}
                name='validateDataChecked'
                disabled={!validTemplateSelected}
              />
            }
            label={t('LBL_VAL_OUT_DATA')}
          />
        </FormControl>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", marginLeft: "30px"}}>
        {/* "Element Validation" checkbox */}
        {(templateDatatype === 'EDI' && treeSelectedNode?.VALIDATE_DATA === "TRUE" && templateDatatype !== "") && (
          <FormControl variant='outlined'size='small'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={elementValidateChecked}
                  onChange={handleElementValidateChange}
                  name='elementValidateChecked'
                  disabled={!validTemplateSelected}
                />
              }
              label={t('LBL_VAL_ELEMENT')}
            />
          </FormControl>
        )}

        {/* "Segment Validation" checkbox */}
        {(templateDatatype === 'EDI' && treeSelectedNode?.VALIDATE_DATA === "TRUE" && templateDatatype !== "") && (
          <FormControl variant='outlined' size='small'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={segmentValidateChecked}
                  onChange={handleSegmentValidateChange}
                  name='segmentValidateChecked'
                  disabled={!validTemplateSelected}
                />
              }
              label={t('LBL_VAL_SEGMENT')}
            />
          </FormControl>
        )}

        {/* "Transaction Set Validation" checkbox */}
        {(templateDatatype === 'EDI' && treeSelectedNode?.VALIDATE_DATA === "TRUE" && templateDatatype !== "") && (
          <FormControl variant='outlined' size='small'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={transactionSetValidateChecked}
                  onChange={handleTransactionSetValidateChange}
                  name='transactionSetValidateChecked'
                  disabled={!validTemplateSelected}
                />
              }
              label={t('LBL_VAL_TRANSACTIONSET')}
            />
          </FormControl>
        )}

        {/* "Functional Group Validation" checkbox */}
        {(templateDatatype === 'EDI' && treeSelectedNode?.VALIDATE_DATA === "TRUE" && templateDatatype !== "") && (
          <FormControl variant='outlined' size='small'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={functionalGroupValidateChecked}
                  onChange={handleFunctionalGroupValidateChange}
                  name='functionalGroupValidateChecked'
                  disabled={!validTemplateSelected}
                />
              }
              label={t('LBL_VAL_FUNCTIONAL_GROUP')}
            />
          </FormControl>
        )}
      </div>

      {/* "Stop Translation on Error" checkbox */}
      {(!['DATABASE', 'XML'].includes(templateDatatype) && treeSelectedNode?.VALIDATE_DATA === "TRUE" && templateDatatype !== "") && (
        <FormControl variant='outlined' className='width-100' size='small'>
          <FormControlLabel
            control={
              <Checkbox
                checked={stopOnFailedChecked}
                onChange={handleStopOnFailedChange}
                name='stopOnFailedChecked'
                disabled={!validTemplateSelected}
              />
            }
            label={t('LBL_STOP_TRANSLATING_ON_ERROR')}
          />
        </FormControl>
      )}

      {(treeSelectedNode && treeSelectedNode.type === "outbound") && (
        <OutboundConditionModal 
          ibTemplate={parentNode?.TEMPLATE?.split(".")?.[0] || ""} 
          ibWorkflow={parentNode?.TEMPLATE_LOCATION?.split("/")?.[1] || ""} 
          node={treeSelectedNode} 
          valuesHandler={handleOutboundModalSave}
          disabled={!validTemplateSelected}
        />
      )}

    </div>
  )
}

const styles = {
  getDataNameAdornment: {
    color: "#666666",
    fontSize: "9px",
    position: "absolute",
    right: "13px",
    top: "-6px",
    backgroundColor: "#FFF",
    zIndex: "2",
    padding: "0 5px",
    height: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  treeHeaderContainer: {
    paddingTop: '8px',
    paddingBottom: '2px',
    paddingLeft: '0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  treeContainer: {
    height: '100%',
    width: '100%',
    border: '1px solid #999',
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
    borderRadius: '4px',
    minHeight: "161px",
    maxHeight: "161px", 
    overflowY: "auto"
  },
  fpContainer: {
		// padding: "0 16px 16px 16px"
		padding: "0 4px 4px 4px",
	},
	accordianContainer: {
		border: "none",
		padding: "0",
		margin: "0",
		boxShadow: "none",
		backgroundColor: "unset",
	},
	accordianHeader: {
		border: "none",
		padding: "0",
		margin: "0",
		backgroundColor: "unset",
	},
	accordianContent: {
		border: "none",
		padding: "0 0px",
		margin: "0",
		backgroundColor: "unset",
	},
};